// ─── Impostazioni → Regole per i soci → 🚦 Disdette (fase B) ──────────
//
// Dal mockup «Prenotazione e disdetta», approvato il 28/09/2026 (A · A · A):
// l'interruttore a tre posizioni — Spento · Solo registro · Acceso — e i
// parametri della specifica (§ 11) in un posto solo. «Acceso» arriva con la
// carta (fase C); i campi della C e della D si vedono ma non si toccano ancora.
//
// 🔤 30/09/2026 (mockup «Senza la C e la D», 1A 2A): sullo schermo le lettere
// delle fasi non ci sono più. «Acceso» ha una riga sotto che dice quando si
// potrà scegliere, e i tre campi che non si toccano stanno insieme nel
// riquadro «Quando arriveranno», in fondo ai Parametri.
//
// 🔔 30/09/2026 (mockup «Premio, Crediti e campi fissi in una sezione», 2A):
// sotto i suoi avvisi, gli «Avvisi sulla partita, all'organizzatore» che
// stavano nella linguetta Acconto. I valori restano dove sono (le impostazioni
// di prima, `/settings/partite`, rimandate indietro intatte tranne `avvisi`), e
// li salva lo stesso «Salva» della pagina.

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ACCESO_DISPONIBILE,
  NON_PRIMA_DELLE_MAX,
  NON_PRIMA_DELLE_MIN,
  STATI_DELLE_REGOLE,
  type ImpostazioniPartite,
  type ImpostazioniDisdette,
  type StatoDelleRegole,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'impostazioni-disdette';

const ETICHETTE: Record<StatoDelleRegole, string> = {
  spento: 'Spento',
  registro: 'Solo registro',
  acceso: 'Acceso',
};

const SPIEGA: Record<StatoDelleRegole, string> = {
  spento: 'Spento: nessuna infrazione si registra, e ai soci non arriva niente.',
  registro:
    'Solo registro: le disdette tardive, gli spostamenti tardivi e i no-show si registrano e li vede la Segreteria. Ai soci non arriva niente e nessuno cambia stato: le infrazioni di questo periodo non conteranno mai.',
  acceso:
    'Acceso: alla prima infrazione il socio prenota con la carta a garanzia, e dopo le partite regolari torna libero.',
};

interface Modulo {
  stato: StatoDelleRegole;
  ore: string;
  oreLezioni: string;
  infrazioni: string;
  partite: string;
  oreIncompleta: string;
  vigilia: string;
  /** 🔔 Gli avvisi all'organizzatore: il giorno prima, le ore prima, mai prima delle. */
  orgVigilia: string;
  orgOrePrima: string;
  orgAlba: string;
}

type Salvate = { disdette: ImpostazioniDisdette; avvisi: ImpostazioniPartite };

const ORARIO = /^([01]\d|2[0-3]):[0-5]\d$/;

const daSalvate = ({ disdette: v, avvisi: a }: Salvate): Modulo => ({
  stato: v.stato,
  ore: String(v.oreDisdetta),
  oreLezioni: String(v.oreDisdettaLezioni),
  infrazioni: String(v.infrazioniPerObbligo),
  partite: String(v.partiteRegolari),
  oreIncompleta: String(v.avvisi.oreIncompleta),
  vigilia: v.avvisi.vigiliaAlle,
  orgVigilia: a.avvisi.vigiliaAlle,
  orgOrePrima: String(a.avvisi.orePrima),
  orgAlba: a.avvisi.nonPrimaDelle,
});

function intero(s: string, min: number, max: number): number | undefined {
  const n = Number(s.trim());
  return Number.isInteger(n) && n >= min && n <= max ? n : undefined;
}

export function Disdette(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const letta = useQuery({
    queryKey: [CHIAVE],
    queryFn: async (): Promise<Salvate> => {
      const [disdette, avvisi] = await Promise.all([
        chiama<ImpostazioniDisdette>('GET', '/settings/booking-rules'),
        chiama<ImpostazioniPartite>('GET', '/settings/partite'),
      ]);
      return { disdette, avvisi };
    },
  });
  if (letta.isLoading) return <Attesa cosa="le regole della disdetta" />;
  if (letta.error || !letta.data) return <Guasto errore={letta.error} />;
  return <ModuloDisdette salvate={letta.data} modificabile={modificabile} />;
}

function ModuloDisdette({
  salvate,
  modificabile,
}: {
  salvate: Salvate;
  modificabile: boolean;
}): React.JSX.Element {
  const salvata = salvate.disdette;
  const rileggi = usaRilettura(CHIAVE);
  const [m, setM] = useState<Modulo>(() => daSalvate(salvate));
  const [salvato, setSalvato] = useState(false);
  useEffect(() => setM(daSalvate(salvate)), [salvate]);
  const cambia = (f: (x: Modulo) => Modulo) => {
    setSalvato(false);
    setM(f);
  };

  const ore = intero(m.ore, 1, 24 * 14);
  const oreLezioni = intero(m.oreLezioni, 1, 24 * 14);
  const infrazioni = intero(m.infrazioni, 1, 10);
  const partite = intero(m.partite, 1, 50);
  const oreIncompleta = intero(m.oreIncompleta, 24, 24 * 7);
  const vigilia = /^([01]\d|2[0-3]):[0-5]\d$/.test(m.vigilia) ? m.vigilia : undefined;
  const orgVigiliaOk = ORARIO.test(m.orgVigilia.trim());
  const orgOrePrima = intero(m.orgOrePrima, 1, 12);
  const alba = m.orgAlba.trim();
  const albaOk = ORARIO.test(alba) && alba >= NON_PRIMA_DELLE_MIN && alba <= NON_PRIMA_DELLE_MAX;
  const pronto =
    ore !== undefined &&
    oreLezioni !== undefined &&
    infrazioni !== undefined &&
    partite !== undefined &&
    oreIncompleta !== undefined &&
    vigilia !== undefined &&
    orgVigiliaOk &&
    orgOrePrima !== undefined &&
    albaOk;

  const salva = useMutation({
    meta: { fatto: 'regole della disdetta salvate' },
    mutationFn: async () => {
      await chiama<ImpostazioniDisdette>('PUT', '/settings/booking-rules', {
        stato: m.stato,
        oreDisdetta: ore,
        oreDisdettaLezioni: oreLezioni,
        infrazioniPerObbligo: infrazioni,
        partiteRegolari: partite,
        avvisi: { oreIncompleta, vigiliaAlle: vigilia },
        ritardoResiduoMinuti: salvata.ritardoResiduoMinuti,
        minutiLinkCarta: salvata.minutiLinkCarta,
        mesiBorsellino: salvata.mesiBorsellino,
      });
      await chiama<ImpostazioniPartite>('PUT', '/settings/partite', {
        // Il campo fisso com'è salvato: di queste impostazioni qui si toccano solo gli avvisi.
        fissoDalBot: salvate.avvisi.fissoDalBot,
        avvisi: { vigiliaAlle: m.orgVigilia.trim(), orePrima: orgOrePrima, nonPrimaDelle: alba },
      });
    },
    onSuccess: () => setSalvato(true),
    // Anche a metà strada: la pagina rilegge quello che è stato salvato davvero.
    onSettled: () => rileggi(),
  });

  return (
    <>
      <div className="riquadro">
        <h3>🚦 Prenotazione e disdetta</h3>
        <p className="tenue">
          Si prenota liberamente, e si disdice gratis fino alle ore qui sotto. Una disdetta più tardi, uno
          spostamento di giorno o di ora più tardi, o un no-show sono un’infrazione di chi ha organizzato: da
          lì prenota con una carta a garanzia, e dopo le partite regolari torna libero. Lezioni, corsi, tornei
          e le cause del circolo non fanno mai un’infrazione.
        </p>
      </div>

      <form
        className="bot-soci"
        onSubmit={(e) => {
          e.preventDefault();
          if (pronto && modificabile) salva.mutate();
        }}
      >
        <SoloLettura mostra={modificabile} />
        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>Le regole</h3>
          <div className="interruttore-regole" role="radiogroup" aria-label="Stato delle regole">
            {STATI_DELLE_REGOLE.map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={m.stato === s}
                disabled={s === 'acceso' && !ACCESO_DISPONIBILE}
                title={
                  s === 'acceso' && !ACCESO_DISPONIBILE
                    ? 'Si potrà scegliere quando i soci potranno lasciare la carta a garanzia'
                    : undefined
                }
                onClick={() => cambia((x) => ({ ...x, stato: s }))}
              >
                {ETICHETTE[s]}
              </button>
            ))}
          </div>
          {!ACCESO_DISPONIBILE && (
            <p className="tenue piccolo">
              «Acceso» si potrà scegliere quando i soci potranno lasciare la carta a garanzia.
            </p>
          )}
          <p className="piccolo">{SPIEGA[m.stato]}</p>
          <p className="tenue piccolo">
            {salvata.avvio
              ? `Accese dal ${new Date(salvata.avvio).toLocaleString('it-IT', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}: le infrazioni contano da lì.`
              : 'Data di avvio: si fissa quando accendi. Le infrazioni contano solo da quel giorno.'}
          </p>
        </fieldset>

        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>Parametri</h3>
          <Campo
            etichetta="Si annulla fino a (ore prima)"
            inputMode="numeric"
            value={m.ore}
            onChange={(e) => cambia((x) => ({ ...x, ore: e.target.value }))}
          />
          {/* 🔒 15/10/2026 (mockup «Il blocco delle 48 ore», 3A): era «Disdetta gratis fino a». */}
          <p className="tenue piccolo">
            Dopo, dal bot la partita non si annulla più: la annulla solo la Segreteria.
          </p>
          {ore === undefined && <p className="guasto">Fra 1 e 336 ore.</p>}
          {/* 🎓 25/10/2026 (bot): le lezioni a parte, 🗣️ «6 ore prima dell'inizio … corretto verso il maestro». */}
          <Campo
            etichetta="Le lezioni si annullano fino a (ore prima)"
            inputMode="numeric"
            value={m.oreLezioni}
            onChange={(e) => cambia((x) => ({ ...x, oreLezioni: e.target.value }))}
          />
          <p className="tenue piccolo">
            Vale per lezioni, allenamenti e partite tattiche: dopo, dal bot non si annullano e non si spostano
            più.
          </p>
          {oreLezioni === undefined && <p className="guasto">Fra 1 e 336 ore.</p>}
          <Campo
            etichetta="Infrazioni che fanno scattare l’obbligo carta"
            inputMode="numeric"
            value={m.infrazioni}
            onChange={(e) => cambia((x) => ({ ...x, infrazioni: e.target.value }))}
          />
          {infrazioni === undefined && <p className="guasto">Fra 1 e 10.</p>}
          <Campo
            etichetta="Partite regolari per tornare liberi"
            inputMode="numeric"
            value={m.partite}
            onChange={(e) => cambia((x) => ({ ...x, partite: e.target.value }))}
          />
          {partite === undefined && <p className="guasto">Fra 1 e 50.</p>}
          <Campo
            etichetta="Avviso «siete in N su 4» (ore prima)"
            inputMode="numeric"
            value={m.oreIncompleta}
            onChange={(e) => cambia((x) => ({ ...x, oreIncompleta: e.target.value }))}
          />
          {oreIncompleta === undefined && <p className="guasto">Fra 24 e 168 ore.</p>}
          <Campo
            etichetta="Avviso della vigilia, alle"
            inputMode="numeric"
            value={m.vigilia}
            onChange={(e) => cambia((x) => ({ ...x, vigilia: e.target.value }))}
          />
          {vigilia === undefined && <p className="guasto">Un orario nella forma 18:00.</p>}
          <p className="tenue piccolo">
            Le ore della disdetta stanno qui e basta: con le regole accese o nel solo registro, l’assistente
            usa queste.
          </p>
          <div className="quando-arriveranno">
            <h4>Quando arriveranno</h4>
            <p className="tenue piccolo">
              Si potranno cambiare quando ci saranno la carta a garanzia e la scadenza del Borsellino. I
              valori sono già salvati.
            </p>
            <Campo
              etichetta="Addebito del residuo (minuti dopo la fine)"
              value={String(salvata.ritardoResiduoMinuti)}
              disabled
              readOnly
            />
            <Campo
              etichetta="Il link per la carta vale (minuti)"
              value={String(salvata.minutiLinkCarta)}
              disabled
              readOnly
            />
            <Campo
              etichetta="Il Borsellino scade (mesi dall’ultima ricarica)"
              value={String(salvata.mesiBorsellino)}
              disabled
              readOnly
            />
          </div>
        </fieldset>

        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>Avvisi sulla partita, all'organizzatore</h3>
          <p className="tenue piccolo">Per tutte le partite. Arrivano solo a chi ha organizzato.</p>
          <Campo
            etichetta="Il giorno prima, alle"
            value={m.orgVigilia}
            onChange={(e) => cambia((x) => ({ ...x, orgVigilia: e.target.value }))}
          />
          <p className="tenue piccolo">
            Solo se la partita non è ancora completa: le idee per trovare chi manca.
          </p>
          {!orgVigiliaOk && <p className="guasto">Scrivi un'ora, per esempio 18:00.</p>}
          <Campo
            etichetta="Il giorno della partita, quante ore prima"
            inputMode="numeric"
            value={m.orgOrePrima}
            onChange={(e) => cambia((x) => ({ ...x, orgOrePrima: e.target.value }))}
          />
          <p className="tenue piccolo">
            Se sono in quattro, la conferma; se manca qualcuno, le idee per completarla.
          </p>
          {orgOrePrima === undefined && <p className="guasto">Fra 1 e 12 ore.</p>}
          <Campo
            etichetta="Mai prima delle"
            value={m.orgAlba}
            onChange={(e) => cambia((x) => ({ ...x, orgAlba: e.target.value }))}
          />
          <p className="tenue piccolo">
            Lo decidi tu; se non lo tocchi vale 07:00. Se il conto cade prima di quest’ora, l’avviso arriva a
            quest’ora.
          </p>
          {!albaOk && (
            <p className="guasto">
              Scrivi un'ora fra le {NON_PRIMA_DELLE_MIN} e le {NON_PRIMA_DELLE_MAX}, per esempio 07:00.
            </p>
          )}
        </fieldset>

        {modificabile && (
          <>
            <Guasto errore={salva.error} />
            {salvato && !salva.isPending && <p className="tenue">Salvato.</p>}
            <button type="submit" disabled={salva.isPending || !pronto}>
              {salva.isPending ? 'Un attimo…' : 'Salva'}
            </button>
          </>
        )}
      </form>
    </>
  );
}
